import * as THREE from 'three';
import { KAELA } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Kaela of the Moss's own effects: moss sprouting where she walks, her thorn shot, roots gripping a hero, moss on a
 * tower, thorns bursting, the pack's call, the Forest coming down over the whole circle, and her turning bear.
 */
const TAU = Math.PI * 2;
const GLOW = '#5fae3a';
const ACCENT = '#3f9a2c';
const PALE = '#dcffc0';
const BARK = '#1e2a18';

export const kaelaFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: '#0d140a', smoke: '#18201a' },
  clips: { kaelaThorns: 'thorns', kaelaPack: 'pack', kaelaBear: 'roar' },
  frame: (c, s, e, p, yaw, h) => {
    const u = e.un;
    if (!u) return;
    const beat = (s.tick + e.id) % 12;
    const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const bear = e.phase >= 2;
    if (e.anim === 'walk' && beat % 6 === 0) {
      // Moss springs up in her footprints (a bear's pawfalls shake the ground).
      const side = beat % 12 === 0 ? 1 : -1;
      const q = p.clone().add(new THREE.Vector3(fwd.z * side * 0.3, 0.02, -fwd.x * side * 0.3));
      c.vfx.ring(q, 0.05, bear ? 0.5 : 0.32, ACCENT, 12, 0.55);
      c.vfx.burst(q, { n: c.low ? 1 : 3, color: '#3e6a2c', speed: 0.02, up: 0.03, size: 0.08, life: 12, gravity: 0.004, spread: 0.15 });
      if (bear) c.shake(0.05);
    }
    // Spores always drift off her.
    if (beat % 4 === 0) c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: 1, color: GLOW, speed: 0.01, up: 0.01, size: 0.05, life: 18, gravity: -0.001, spread: 0.35 });
    if (bear && e.anim === 'attack' && e.animT === 2 && u.cast === 0) {
      // The bear's swipe lands in front of her.
      const hit = p.clone().addScaledVector(fwd, 1.0);
      c.vfx.ring(hit, 0.1, 1.1, ACCENT, 7, 1);
      c.vfx.burst(hit.clone().setY(hit.y + 0.3), { n: c.low ? 4 : 10, color: PALE, speed: 0.08, up: 0.08, size: 0.08, life: 8, gravity: 0.012 });
      c.shake(0.14);
    }
  },
  skill: (c, _s, e, name, p) => {
    if (name === 'kaelaPack') {
      // The wolves come out of the ground round the hero in a ring of green.
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 1.6, ACCENT, 14, 0.9);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: c.low ? 6 : 14, color: BARK, speed: 0.06, up: 0.04, size: 0.4, life: 16, smoke: true, spread: 0.8 });
      if (e) c.flare(c.at(e.x, e.y, 2.2), GLOW, 1.6, 12);
      return true;
    }
    if (name === 'kaelaThorns') {
      if (e) c.flare(c.at(e.x, e.y, 1.8), GLOW, 1.4, 10);
      return true;
    }
    if (name === 'kaelaMoss') return true;
    if (name === 'kaelaBear') {
      // Her robe bursts into moss and smoke: the bear rises out of it.
      c.vfx.burst(p.clone().setY(p.y + 0.8), { n: c.low ? 10 : 26, color: '#18201a', speed: 0.08, up: 0.06, size: 0.8, life: 22, smoke: true, spread: 0.6 });
      c.vfx.burst(p.clone().setY(p.y + 0.8), { n: c.low ? 8 : 20, color: GLOW, speed: 0.1, up: 0.1, size: 0.1, life: 14, gravity: 0.008 });
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 2.4, ACCENT, 16, 1);
      c.flare(p.clone().setY(p.y + 1.2), GLOW, 4, 16);
      c.shake(0.5);
      return true;
    }
    return false;
  },
  beam: (c, kind, a, b) => {
    if (kind !== 'kaelaShot') return false;
    // A thorn of green light from her staff's head to the hero.
    const from = a.clone().setY(a.y + 1.6);
    const to = b.clone().setY(b.y + 0.6);
    c.vfx.beam(from, to, GLOW, 6, 0.03, 0.15);
    c.vfx.beam(from, to, PALE, 4, 0.012, 0);
    c.vfx.burst(to, { n: c.low ? 3 : 7, color: ACCENT, speed: 0.05, up: 0.04, size: 0.07, life: 8, gravity: 0.01 });
    c.flare(from, GLOW, 0.9, 6);
    return true;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'kaelaRoots') {
      // Roots burst out of the ground and close round the hero.
      for (let i = 0; i < (low ? 4 : 7); i++) {
        const a = (i / (low ? 4 : 7)) * TAU;
        const base = p.clone().add(new THREE.Vector3(Math.cos(a) * 0.55, 0.02, Math.sin(a) * 0.55));
        c.vfx.beam(base, p.clone().setY(p.y + 0.7), '#2b3a20', 14, 0.05, 0.1);
        c.vfx.beam(base, p.clone().setY(p.y + 0.55), ACCENT, 10, 0.015, 0);
      }
      c.vfx.ring(p.clone().setY(p.y + 0.04), 0.1, 0.8, ACCENT, 10, 0.9);
      c.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 10, color: '#3a2e22', speed: 0.05, up: 0.05, size: 0.25, life: 12, smoke: true, spread: 0.3 });
      return true;
    }
    if (kind === 'kaelaMoss') {
      // Moss swarming up a tower.
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.0, ACCENT, 12, 0.8);
      c.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 5 : 12, color: '#3e6a2c', speed: 0.04, up: 0.06, size: 0.18, life: 16, gravity: 0.002, spread: 0.4 });
      c.flare(p.clone().setY(p.y + 1.0), GLOW, 1.4, 10);
      return true;
    }
    if (kind === 'kaelaThorns') {
      // Thorns of black glass burst up out of the mark.
      const n = low ? 6 : 12;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * TAU + i * 0.4;
        const d = r * (0.25 + 0.7 * ((i * 37) % 10) / 10);
        const base = p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.02, Math.sin(a) * d));
        c.vfx.beam(base, base.clone().add(new THREE.Vector3(Math.cos(a) * 0.15, 0.6 + 0.4 * ((i * 13) % 5) / 5, Math.sin(a) * 0.15)), '#1a2216', 10, 0.05, 0.05);
      }
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, Math.max(0.8, r), GLOW, 9, 1);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 5 : 14, color: PALE, speed: 0.09, up: 0.1, size: 0.08, life: 10, gravity: 0.012 });
      c.flare(p.clone().setY(p.y + 0.5), GLOW, 1.8, 8);
      c.shake(0.18);
      return true;
    }
    if (kind !== 'kaelaForest') return false;
    // The Forest comes: black trunks of smoke rise round the whole circle, a wave of green rolls out from her.
    if (e) c.force(e.id, 'forest');
    const R = (KAELA.forest.radius / 1000) * WORLD_PER_HEX;
    const steps = low ? 8 : 16;
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * TAU;
      for (const k of [0.5, 0.85]) {
        const q = p.clone().add(new THREE.Vector3(Math.cos(a + k) * R * k, 0.3, Math.sin(a + k) * R * k));
        c.vfx.burst(q, { n: low ? 2 : 4, color: '#0f160d', speed: 0.02, up: 0.09, size: 0.9, life: 30, smoke: true, spread: 0.25 });
        c.vfx.burst(q, { n: low ? 1 : 3, color: GLOW, speed: 0.04, up: 0.08, size: 0.1, life: 18, gravity: -0.002 });
      }
    }
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, R, ACCENT, 16, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, R * 0.6, PALE, 20, 0.7);
    c.vfx.implode(p.clone().setY(p.y + 1), 2.5, { n: low ? 10 : 24, color: GLOW, size: 0.1, life: 14 });
    c.flare(p.clone().setY(p.y + 1.2), GLOW, 7, 16);
    c.shake(0.85);
    return true;
  },
};
